import { useState } from 'react';
import API_CONFIG from '../../config/api';
import './CreateAdminModal.css';

function CreateAdminModal({ isOpen, onClose, onSuccess, createdBy }) {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'admin'
    });
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const permissionCategories = {
        'User Verification': [
            { key: 'kyc.manage', label: 'KYC Management', icon: '🆔' },
            { key: 'users.verify.kyc', label: 'Verify KYC Documents', icon: '📄' },
            { key: 'users.view_kyc', label: 'View KYC Data', icon: '🔍' }
        ],
        'Loan Operations': [
            { key: 'loans.view', label: 'View Loans', icon: '💼' },
            { key: 'loans.approve', label: 'Approve Loans', icon: '⭐' },
            { key: 'loans.edit', label: 'Edit Loan Terms', icon: '✏️' },
            { key: 'loans.disburse', label: 'Disburse Funds', icon: '📤' }
        ],
        'User Management': [
            { key: 'users.view_all', label: 'View All Users', icon: '👥' },
            { key: 'users.edit', label: 'Edit Users', icon: '✏️' },
            { key: 'users.delete', label: 'Delete Users', icon: '🗑️' },
            { key: 'users.create', label: 'Create Users', icon: '➕' }
        ],
        'Sales Operations': [
            { key: 'leads.view', label: 'View All Leads', icon: '👁️' },
            { key: 'leads.create', label: 'Create Leads', icon: '📝' },
            { key: 'leads.edit', label: 'Edit Leads', icon: '✏️' },
            { key: 'leads.assign', label: 'Assign Leads', icon: '👤' },
            { key: 'leads.status', label: 'Update Lead Status', icon: '🔄' }
        ],
        'Support Operations': [
            { key: 'tickets.manage', label: 'Manage Tickets', icon: '🎫' },
            { key: 'support.view', label: 'View Tickets', icon: '👁️' },
            { key: 'support.assign', label: 'Assign Tickets', icon: '👥' },
            { key: 'support.resolve', label: 'Resolve Tickets', icon: '✅' }
        ],
        'System Management': [
            { key: 'admins.view', label: 'View Admins', icon: '🛡️' },
            { key: 'admins.manage', label: 'Manage Admins', icon: '⚙️' },
            { key: 'settings.manage', label: 'System Settings', icon: '🛠️' },
            { key: 'database.manage', label: 'Database Ops', icon: '💾' },
            { key: 'security.manage', label: 'Security Policies', icon: '🔐' },
            { key: 'permissions.manage', label: 'Permission Control', icon: '🔑' },
            { key: 'api.manage', label: 'API Management', icon: '🔌' },
            { key: 'notifications.manage', label: 'Notifications', icon: '🔔' },
            { key: 'payments.manage', label: 'Payment Gateways', icon: '💳' },
            { key: 'admin.impersonate', label: 'Login as User', icon: '🎭' },
            { key: 'users.temp_password', label: 'Temp Passwords', icon: '⏳' },
            { key: 'users.agreement_upload', label: 'Upload Agreements', icon: '📄' },
            { key: 'system.health', label: 'System Health', icon: '🏥' }
        ],
        'Reporting': [
            { key: 'analytics.view', label: 'View Analytics', icon: '📊' },
            { key: 'reports.view', label: 'View Reports', icon: '📋' },
            { key: 'logs.view', label: 'Audit Logs', icon: '📜' },
            { key: 'activity.view', label: 'Activity Monitor', icon: '📡' }
        ]
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handlePermissionToggle = (permKey) => {
        if (formData.role === 'super_admin') return;

        setSelectedPermissions(prev =>
            prev.includes(permKey)
                ? prev.filter(p => p !== permKey)
                : [...prev, permKey]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
            setError('All fields are required');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setIsLoading(true);

        // Fallback for createdBy if not passed correctly via prop
        const effectiveCreatedBy = createdBy || JSON.parse(localStorage.getItem('adminSession'))?.userId;

        try {
            const response = await fetch(API_CONFIG.ENDPOINTS.ADMIN_CREATE_ADMIN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': effectiveCreatedBy
                },
                body: JSON.stringify({
                    ...formData,
                    permissions: selectedPermissions,
                    createdBy: effectiveCreatedBy
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setError(data.message || 'Failed to create admin');
                setIsLoading(false);
                return;
            }

            // Success
            setIsLoading(false);
            setFormData({ email: '', password: '', firstName: '', lastName: '', phone: '', role: 'admin' });
            setSelectedPermissions([]);
            onSuccess(data.data);
            onClose();

        } catch (error) {
            setIsLoading(false);
            setError('Unable to connect to server');
            console.error('Create admin error:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content create-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>🔐 Create Admin Account</h2>
                    <button className="modal-close-btn" onClick={onClose}>×</button>
                </div>

                {error && (
                    <div className="modal-error">
                        ⚠️ {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="create-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="firstName">First Name *</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="John"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="lastName">Last Name *</label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder="Doe"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="admin@efin.co.in"
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone Number</label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="10-digit number"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Minimum 8 characters"
                            required
                            disabled={isLoading}
                            minLength="8"
                        />
                        <small className="form-hint">Minimum 8 characters, include uppercase, lowercase, number & special character</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="role">Assign Role *</label>
                        <select
                            id="role"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="modal-select"
                            disabled={isLoading}
                            required
                        >
                            <option value="admin">Administrator</option>
                            <option value="sales">Sales Staff</option>
                            <option value="support">Support Specialist</option>
                            <option value="collection">Collections</option>
                            <option value="branch">Branch Ops</option>
                            <option value="accounts">Accounts</option>
                            <option value="super_admin">Super Admin (High Level)</option>
                        </select>
                    </div>

                    <div className="form-section">
                        <h3>🔑 Feature Access Permissions</h3>
                        <p className="section-hint">Toggle specific features this admin can access. Super Admins have all permissions by default.</p>

                        {formData.role === 'super_admin' ? (
                            <div className="all-access-alert">
                                👑 <strong>Super Admin</strong> has all permissions enabled by default.
                            </div>
                        ) : (
                            Object.entries(permissionCategories).map(([category, perms]) => (
                                <div key={category} className="permission-category-group">
                                    <h4>{category}</h4>
                                    <div className="permission-grid">
                                        {perms.map(perm => (
                                            <div
                                                key={perm.key}
                                                className={`permission-pill ${selectedPermissions.includes(perm.key) ? 'active' : ''}`}
                                                onClick={() => handlePermissionToggle(perm.key)}
                                            >
                                                <span className="p-icon">{perm.icon}</span>
                                                <span className="p-label">{perm.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <span className="spinner"></span>
                                    Creating...
                                </>
                            ) : (
                                '✅ Create Admin'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateAdminModal;
